"use client"

import { useEffect, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { Database, TrendingUp } from "lucide-react"
import { api } from "@/lib/api"
import type { LicenseUsageResponse } from "@/lib/types"

const formatNumber = (num: number) => num.toLocaleString("ja-JP")

export function LicenseUsageWidget() {
  const [data, setData] = useState<LicenseUsageResponse | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api.get<LicenseUsageResponse>("/dashboard/license-usage")
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const currentRecords = data?.current_records ?? 0
  const maxRecords = data?.max_records ?? 1
  const usagePercent = data?.usage_percentage ?? 0
  const remaining = Math.max(maxRecords - currentRecords, 0)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">ライセンス使用状況</CardTitle>
        </div>
        <CardDescription>保存レコード数の使用状況</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-16 w-full" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNumber(currentRecords)}
                  </p>
                  <p className="text-sm text-muted-foreground">件</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">上限</p>
                  <p className="text-lg font-semibold text-foreground">
                    {formatNumber(maxRecords)} 件
                  </p>
                </div>
              </div>

              <Progress value={usagePercent} className="h-3" />

              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">使用率</span>
                <span className="font-medium text-foreground">{usagePercent.toFixed(1)}%</span>
              </div>
            </div>

            <div className="rounded-lg bg-secondary/50 p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-success" />
                <span className="text-sm text-muted-foreground">今月の増加</span>
              </div>
              <p className="mt-1 text-sm font-medium">
                +{formatNumber(data?.monthly_growth ?? 0)} 件
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">残り容量</p>
                <p className="mt-1 text-sm font-medium text-foreground">
                  {formatNumber(remaining)} 件
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-3">
                <p className="text-xs text-muted-foreground">予想到達日</p>
                <p className="mt-1 text-sm font-medium text-foreground">
                  {data?.months_until_full ? `約${data.months_until_full}ヶ月後` : "---"}
                </p>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
